import Axios, { AxiosRequestConfig } from 'axios';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { taApiClient } from '../utils/http-client';

export default class ResourceManager {
	getPaginatedListFetcher = ({ fetcher, beforeFetch, distinct, afterFetch, debounce = 300 }: any): any => {
		let pipeline: any = [];
		if (debounce) pipeline.push(debounceTime(debounce));
		if (distinct) pipeline.push(distinctUntilChanged());
		if (beforeFetch) pipeline.push(tap(v => beforeFetch(v)));
		pipeline.push(
			switchMap(v => {
				return new Observable(observer => {
					let cancelToken = Axios.CancelToken.source();
					fetcher(v, {
						cancelToken: cancelToken?.token
					})
						.then((resp: any) => {
							observer.next(resp);
							observer.complete();
						})
						.catch((e: any) => observer.error(e));
					return () => cancelToken?.cancel();
				});
			})
		);
		if (afterFetch) pipeline.push(tap(v => afterFetch(v)));
		const subject = new Subject();
		return subject.pipe.apply(subject, pipeline);
	};

	async getResource<T>(url: string, options: AxiosRequestConfig | undefined = undefined): Promise<T | null> {
		let response = await taApiClient.get(url, options);
		if (response.status === 500) return null;
		return response.data;
	}

	async postResource<T, L>(
		url: string,
		data: T,
		options: AxiosRequestConfig | undefined = undefined
	): Promise<L | null> {
		let response = await taApiClient.post(url, data, options);
		if (response.status === 500) return null;
		return response.data;
	}

	async putResource<T, L>(
		url: string,
		data: T,
		options: AxiosRequestConfig | undefined = undefined
	): Promise<L | null> {
		let response = await taApiClient.put(url, data, options);
		if (response.status === 500) return null;
		return response.data;
	}

	async patchResource<T, L>(
		url: string,
		data: T,
		options: AxiosRequestConfig | undefined = undefined
	): Promise<L | null> {
		let response = await taApiClient.patch(url, data, options);
		if (response.status === 500) return null;
		return response.data;
	}

	async deleteResource<L>(url: string, options: AxiosRequestConfig | undefined = undefined): Promise<L | null> {
		let response = await taApiClient.delete(url, options);
		if (response.status === 500) return null;
		return response.data;
	}
}
